/**
 * Drives the footwear size-table's horizontal scroll from a single source of truth.
 *
 * Each row is a static `overflow: hidden` pill (clean at any device-pixel-ratio) with
 * a frozen badge and a clipped values track. Rather than give each row its own native
 * scroller (which only scrolls when the cursor is over that row, and whose metrics are
 * unreliable while the drawer is hidden), this element keeps one `scrollX` value and
 * applies it to every row's values via `transform` — so the columns are always in sync
 * and scrolling works anywhere on the table.
 *
 * Input: mouse/trackpad wheel (anywhere), touch drag (anywhere; vertical still
 * scrolls the drawer via `touch-action: pan-y`), and dragging the scrollbar thumb.
 * A touch also flags the row under the finger (`size-table__row--touched`) so its
 * badge fills like the desktop hover, until pointerup/pointercancel.
 *
 * The scrollbar (bar/thumb) mirrors `scrollX`. Its geometry is derived from the
 * already-measured `maxScroll` (content width = track width + maxScroll), NEVER from
 * the values element's offsetWidth — Safari under-reports a `width: max-content` flex
 * container's offsetWidth as the track width, which made a previous version render a
 * full-width, immobile thumb. Sizing/positioning is done in px (translate3d) so the
 * drag math and the visual can't disagree.
 *
 * The class lives inside the `customElements.get` guard so the script is safe to load
 * more than once (the size guide can render in several places).
 */
if (!customElements.get('size-guide-scroll-sync')) {
    const MIN_THUMB_PX = 24;
    /** px per wheel "line" for browsers reporting DOM_DELTA_LINE (Firefox mouse wheels). */
    const WHEEL_LINE_PX = 16;
    class SizeGuideScrollSync extends HTMLElement {
        // Shared scroll position across the instances of one table (its CM and IN copies,
        // which the CM/IN toggle swaps via display). Stored as progress (0..1) so it
        // transfers cleanly even if a unit's columns are slightly wider than the other's.
        static groupScroll = new Map();
        group = null;
        values = [];
        bar = null;
        thumb = null;
        resizeObserver = null;
        scrollX = 0;
        maxScroll = 0;
        thumbMaxOffset = 0;
        startX = 0;
        startScroll = 0;
        dragging = 'none';
        touchedRow = null;
        snapPoints = [];
        snapTimer = 0;
        wheelSettleTimer = 0;
        connectedCallback() {
            this.group = this.getAttribute('data-scroll-group');
            this.values = Array.from(this.querySelectorAll('[data-values]'));
            this.bar = this.querySelector('[data-scroll-bar]');
            this.thumb = this.querySelector('[data-scroll-thumb]');
            this.addEventListener('wheel', this.handleWheel, { passive: false });
            this.addEventListener('pointerdown', this.handleSurfaceDown);
            this.thumb?.addEventListener('pointerdown', this.handleThumbDown);
            // Measure now and after layout settles (the drawer is hidden at parse time),
            // and whenever the component resizes (drawer opens, unit toggles, viewport).
            this.measure();
            requestAnimationFrame(() => this.measure());
            this.resizeObserver = new ResizeObserver(() => this.measure());
            this.resizeObserver.observe(this);
        }
        disconnectedCallback() {
            this.removeEventListener('wheel', this.handleWheel);
            this.removeEventListener('pointerdown', this.handleSurfaceDown);
            this.thumb?.removeEventListener('pointerdown', this.handleThumbDown);
            this.resizeObserver?.disconnect();
            window.clearTimeout(this.snapTimer);
            window.clearTimeout(this.wheelSettleTimer);
            this.endDrag();
        }
        get track() {
            return this.values[0]?.parentElement ?? null;
        }
        measure() {
            const first = this.values[0];
            const track = this.track;
            if (!first || !track)
                return;
            const trackWidth = track.clientWidth;
            // Hidden (display:none via the CM/IN toggle) reports a 0-width track. Bail so
            // we neither clobber the shared position nor clamp our own scrollX to 0 while
            // off-screen — the visible copy stays the single source of truth.
            if (trackWidth === 0)
                return;
            // scrollWidth (the cells' actual extent) not offsetWidth — Safari under-reports
            // a `width: max-content` flex container's offsetWidth, which capped the scroll.
            this.maxScroll = Math.max(0, first.scrollWidth - trackWidth);
            // Snap targets = each column's left edge, expressed as a scrollX value.
            // Normalised on the first cell so the base doesn't depend on offsetParent.
            const cells = Array.from(first.children);
            const base = cells[0]?.offsetLeft ?? 0;
            this.snapPoints = cells.map((c) => c.offsetLeft - base);
            this.measureThumb(trackWidth);
            // Adopt the group's shared position (set by the other unit's copy as it scrolled)
            // so toggling CM/IN keeps the same relative scroll. Runs here because the toggle
            // (display:none → block) triggers the ResizeObserver, and its callback fires
            // between layout and paint — the transform lands before the copy is shown.
            if (this.group !== null) {
                const progress = SizeGuideScrollSync.groupScroll.get(this.group);
                if (progress !== undefined)
                    this.scrollX = progress * this.maxScroll;
            }
            this.setScroll(this.scrollX); // re-clamp + re-apply (also repositions the thumb)
        }
        /* Bar visibility + thumb width. Only on (re)measure — setScroll just translates. */
        measureThumb(trackWidth) {
            if (!this.bar || !this.thumb)
                return;
            this.bar.hidden = this.maxScroll <= 0;
            if (this.bar.hidden)
                return;
            const barWidth = this.bar.clientWidth;
            const contentWidth = trackWidth + this.maxScroll;
            const thumbWidth = Math.max((trackWidth / contentWidth) * barWidth, MIN_THUMB_PX);
            this.thumbMaxOffset = Math.max(0, barWidth - thumbWidth);
            this.thumb.style.width = `${thumbWidth}px`;
        }
        setScroll(x) {
            this.scrollX = Math.max(0, Math.min(x, this.maxScroll));
            // Persist as progress (0..1) into the shared group so the other unit's copy
            // can adopt it on toggle. Only the visible copy reaches here (hidden ones bail
            // in measure and can't receive input), so it stays the single writer.
            if (this.group !== null && this.maxScroll > 0) {
                SizeGuideScrollSync.groupScroll.set(this.group, this.scrollX / this.maxScroll);
            }
            // translate3d (not translateX) to force a GPU layer — avoids Safari's
            // transform + overflow:hidden paint/tiling glitches.
            const offset = `translate3d(${-this.scrollX}px, 0, 0)`;
            for (const values of this.values) {
                values.style.transform = offset;
            }
            this.updateThumb();
        }
        updateThumb() {
            if (!this.thumb || !this.bar || this.bar.hidden || this.maxScroll <= 0)
                return;
            const progress = this.scrollX / this.maxScroll;
            this.thumb.style.transform = `translate3d(${progress * this.thumbMaxOffset}px, 0, 0)`;
        }
        nearestSnap(x) {
            if (!this.snapPoints.length)
                return x;
            // setScroll clamps afterwards, so an out-of-range last column lands at maxScroll.
            return this.snapPoints.reduce((best, p) => (Math.abs(p - x) < Math.abs(best - x) ? p : best));
        }
        // Snap-on-settle: at gesture end, ease to the nearest column's left edge.
        // Arrow property so it can be passed to setTimeout without losing `this`.
        snap = () => {
            if (this.maxScroll <= 0)
                return;
            this.classList.add('is-snapping');
            this.setScroll(this.nearestSnap(this.scrollX));
            window.clearTimeout(this.snapTimer);
            // One timer rather than transitionend, which would fire once per row.
            this.snapTimer = window.setTimeout(() => this.classList.remove('is-snapping'), 180);
        };
        handleWheel = (event) => {
            if (this.maxScroll <= 0)
                return;
            const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
            if (delta === 0)
                return;
            // Let the page/drawer scroll through once we're at an edge.
            if ((delta < 0 && this.scrollX <= 0) || (delta > 0 && this.scrollX >= this.maxScroll))
                return;
            event.preventDefault();
            // Firefox reports physical mouse wheels in lines (deltaMode 1), not pixels —
            // without this a wheel notch moves ~3px. Chrome/Safari/trackpads are already px.
            let scale = 1;
            if (event.deltaMode === WheelEvent.DOM_DELTA_LINE)
                scale = WHEEL_LINE_PX;
            else if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE)
                scale = this.clientWidth;
            this.classList.remove('is-snapping');
            this.setScroll(this.scrollX + delta * scale);
            window.clearTimeout(this.wheelSettleTimer);
            this.wheelSettleTimer = window.setTimeout(this.snap, 120);
        };
        handleSurfaceDown = (event) => {
            // Touch only — desktop uses the wheel; mouse-drag would fight text selection.
            if (event.pointerType !== 'touch')
                return;
            // Touch counterpart of the desktop row hover: fill the touched row's badge
            // until release. Runs even when nothing overflows (drag is a no-op then).
            this.touchedRow = event.target.closest('.size-table__row');
            this.touchedRow?.classList.add('size-table__row--touched');
            this.beginDrag('surface', event.clientX);
        };
        handleThumbDown = (event) => {
            if (this.maxScroll <= 0)
                return;
            // Don't let the surface (touch-drag) handler double-drive the same gesture.
            event.stopPropagation();
            event.preventDefault();
            this.beginDrag('thumb', event.clientX);
        };
        beginDrag(mode, clientX) {
            this.classList.remove('is-snapping');
            this.dragging = mode;
            this.startX = clientX;
            this.startScroll = this.scrollX;
            window.addEventListener('pointermove', this.handlePointerMove);
            window.addEventListener('pointerup', this.handlePointerUp);
            // pointercancel: native vertical scroll (pan-y) taking over the gesture —
            // end the drag and clear the row highlight, or both would stick.
            window.addEventListener('pointercancel', this.handlePointerUp);
        }
        handlePointerMove = (event) => {
            if (this.dragging === 'none')
                return;
            const dx = event.clientX - this.startX;
            if (this.dragging === 'surface') {
                this.setScroll(this.startScroll - dx);
            }
            else if (this.thumbMaxOffset > 0) {
                // Thumb drag: map bar pixels to content pixels via the cached geometry.
                this.setScroll(this.startScroll + (dx / this.thumbMaxOffset) * this.maxScroll);
            }
        };
        handlePointerUp = () => {
            const wasDragging = this.dragging !== 'none';
            this.endDrag();
            if (wasDragging)
                this.snap();
        };
        endDrag() {
            this.dragging = 'none';
            this.touchedRow?.classList.remove('size-table__row--touched');
            this.touchedRow = null;
            window.removeEventListener('pointermove', this.handlePointerMove);
            window.removeEventListener('pointerup', this.handlePointerUp);
            window.removeEventListener('pointercancel', this.handlePointerUp);
        }
    }
    customElements.define('size-guide-scroll-sync', SizeGuideScrollSync);
}
